import { useCallback, useState } from 'react';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { flagVariant } from '../generation/generationApi';

export interface FlagFlow {
  /** The reason dialog is on screen. */
  isOpen: boolean;
  /** This variant has been reported in this session. */
  isFlagged: boolean;
  isSubmitting: boolean;
  error: string | null;
  open: () => void;
  close: () => void;
  submit: (reason: string) => void;
}

/** Message for the domain errors a flag can provoke. */
function flagMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'VARIANT_NOT_FOUND':
        return 'That draft is no longer here. Reload the post and report it again.';
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That report did not send. Try again.');
}

/**
 * Reporting one draft variant to Autopostmate staff.
 *
 * Scoped to a single variant, so a card owns its own dialog and its own
 * "flagged" state and the other variant is untouched. The state is per session
 * rather than read back from the server: the endpoint allows duplicates on
 * purpose (two complaints are two reports), so there is nothing on the request
 * to read a "was flagged" flag out of.
 *
 * Flagging is never a gate. It sets no busy state anyone else reads, and the
 * caller must not disable approve, reject or select while a report is open —
 * the user keeps control of what ships (SRS §3, stage 3).
 */
export function useFlagVariant(
  businessId: string,
  requestId: string,
  variantId: string | null,
): FlagFlow {
  const [isOpen, setIsOpen] = useState(false);
  const [isFlagged, setIsFlagged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback(() => {
    setError(null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setError(null);
    setIsOpen(false);
  }, []);

  const submit = useCallback(
    (reason: string) => {
      if (isSubmitting || !variantId) return;

      setError(null);
      setIsSubmitting(true);

      void (async () => {
        try {
          await flagVariant(businessId, requestId, { variant_id: variantId, reason });
          setIsSubmitting(false);
          setIsOpen(false);
          setIsFlagged(true);
        } catch (caught) {
          setIsSubmitting(false);
          setError(flagMessage(caught));
        }
      })();
    },
    [businessId, isSubmitting, requestId, variantId],
  );

  return { isOpen, isFlagged, isSubmitting, error, open, close, submit };
}
